import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';

import { ConfirmationModalComponent, LoadingSpinnerComponent, RxAlertModule, RxButtonModule, SkeletonGridComponent } from '@roomex/rx-components';
import { CompanyPaymentSettingsFacade, DataAccessModule } from '@rx-applications/company-payment/data-access';
import { SavedPaymentMethodsComponent } from '@rx-applications/company-payment/ui';
import { FeatureAddPaymentMethodComponent } from '@rx-applications/company-payment/feature-add-payment-method';

import { translationProvider, TRANSLATION_SCOPE } from './assets/i18n/translation.provider';
import { ActivatedRoute, Data } from '@angular/router';
import { first, map } from 'rxjs/operators';
import { AppVersionService } from '@rx-applications/shared/data-access';
import { ENVIRONMENT_TOKEN } from '@rx-applications/shared/utils';
import { CreateCustomerRequest, SharedWebsiteData } from '@rx-applications/company-payment/utils';
import { combineLatest } from 'rxjs';

@Component({
  selector: 'rx-applications-feature-company-payment',
  standalone: true,
  templateUrl: './feature-company-payment.component.html',
  styleUrls: ['./feature-company-payment.component.scss'],
  providers: [translationProvider],
  imports: [
    CommonModule,
    TranslocoModule,
    NgbModalModule,
    DataAccessModule,
    RxAlertModule,
    RxButtonModule,
    SavedPaymentMethodsComponent,
    SkeletonGridComponent
  ],
})
export class FeatureCompanyPaymentComponent implements OnInit, OnDestroy {
  readonly TRANSLATION_SCOPE = TRANSLATION_SCOPE;

  companySettingsFacade = inject(CompanyPaymentSettingsFacade);
  #modalService = inject(NgbModal);
  #translocoService = inject(TranslocoService);
  #activatedRoute = inject(ActivatedRoute);
  #appVersionService = inject(AppVersionService);
  #appVersion = inject(ENVIRONMENT_TOKEN).version;

  companyName$ = this.#activatedRoute.data.pipe(map(({ websiteData }) => websiteData?.companyName));

  ngOnInit(): void {
    this.#appVersionService.bindToDOM('app-company-payment-version', this.#appVersion);
    this.companySettingsFacade.getCustomer();

    combineLatest([this.companySettingsFacade.isNewCustomer$, this.#activatedRoute.data])
      .pipe(
        first(([isNewCustomer, data]) => isNewCustomer && data?.websiteData?.companyName),
        map(([_, data]: [boolean, Data]) => {
          const websiteData = data.websiteData as SharedWebsiteData;
          const request: CreateCustomerRequest = {
            name: websiteData.companyName,
            email: websiteData.email,
            currency: websiteData.defaultCurrency,
          };
          return request;
        })
      )
      .subscribe((createCustomerRequest) => {
        this.companySettingsFacade.createCustomer(createCustomerRequest);
      });
  }

  ngOnDestroy(): void {
    this.#modalService.dismissAll();
  }

  openAddPaymentMethodModal(): void {
    this.#modalService.open(FeatureAddPaymentMethodComponent, { size: '565px' });
    this.companySettingsFacade.createSetupIntent();
  }

  onDeletePaymentMethod(id: string): void {
    const confirmCancelModal = this.#modalService.open(ConfirmationModalComponent, { size: '300px' });
    confirmCancelModal.componentInstance.title = this.#getTranslation('message');
    confirmCancelModal.componentInstance.cancelLabel = this.#getTranslation('no');
    confirmCancelModal.componentInstance.confirmLabel = this.#getTranslation('yes');
    confirmCancelModal.result
      .then(() => this.companySettingsFacade.deletePaymentMethod(id))
      .catch(() => {
        /* do nothing*/
      });
  }

  onSetDefaultPaymentMethod(id: string): void {
    this.companySettingsFacade.setDefaultPaymentMethod(id);
  }

  #getTranslation(item: string): string {
    const translationRoot = `${this.TRANSLATION_SCOPE}.confirmationModal`;
    return this.#translocoService.translate(`${translationRoot}.${item}`);
  }
}
